import { createContext, useContext, useEffect, useState, type FC, type ReactNode } from "react";
import type { CuttlefishConfig, CuttlefishConnection } from "../types";
import { resetAdbMessageEmitter } from "../lib/adbMessageEmitter";

interface StreamInfo {
  displayStreams: number[];
  audioStreams: string[];
}

/**
 * Device control message event types
 */
export interface CuttlefishDeviceEvent {
  event: string;
  metadata?: Record<string, any>;
  [key: string]: any;
}

/**
 * Event handlers that can be registered with the provider
 */
export interface CuttlefishEventHandlers {
  onDeviceBoot?: () => void;
  onScreenChanged?: (rotation: number) => void;
  onCaptureImage?: () => void;
  onDisplayPowerModeChanged?: (displayId: number, mode: string) => void;
  onControlMessage?: (event: CuttlefishDeviceEvent) => void;
  onLightsMessage?: (data: any) => void;
  onSensorsMessage?: (data: any) => void;
}

interface CuttlefishContextValue {
  config: CuttlefishConfig;
  connection: CuttlefishConnection | null;
  setConnection: (connection: CuttlefishConnection | null) => void;
  streams: StreamInfo;
  eventHandlers: CuttlefishEventHandlers;
  registerEventHandlers: (handlers: Partial<CuttlefishEventHandlers>) => void;
}

const CuttlefishContext = createContext<CuttlefishContextValue | null>(null);

export interface CuttlefishProviderProps {
  children: ReactNode;
  config: CuttlefishConfig;
  connection?: CuttlefishConnection | null;
  onConnectionChange?: (connection: CuttlefishConnection | null) => void;
  eventHandlers?: CuttlefishEventHandlers;
}

/**
 * Parse stream IDs from connection to identify display and audio streams
 */
const parseStreams = (connection: CuttlefishConnection | null): StreamInfo => {
  if (!connection) {
    return { displayStreams: [], audioStreams: [] };
  }

  const displayStreams: number[] = [];
  const audioStreams: string[] = [];

  // Get all stream IDs from the connection's streams object
  // The streams object is populated by the WebRTC track event
  const streams = connection.allStreams || {};

  Object.keys(streams).forEach((streamId) => {
    const stream = streams[streamId];
    if (!stream || !stream.active) {
      return;
    }

    if (streamId.startsWith('display_')) {
      // Extract display number from "display_0", "display_1", etc.
      const displayNum = parseInt(streamId.substring('display_'.length), 10);
      if (!isNaN(displayNum)) {
        displayStreams.push(displayNum);
      }
    } else if (streamId.startsWith('audio')) {
      audioStreams.push(streamId);
    }
  });

  // Sort display streams numerically
  displayStreams.sort((a, b) => a - b);

  return { displayStreams, audioStreams };
};

export const CuttlefishProvider: FC<CuttlefishProviderProps> = ({
  children,
  config,
  connection = null,
  onConnectionChange,
  eventHandlers: initialEventHandlers = {},
}) => {
  const [streams, setStreams] = useState<StreamInfo>(() => parseStreams(connection));
  const [eventHandlers, setEventHandlers] = useState<CuttlefishEventHandlers>(initialEventHandlers);

  const setConnection = (conn: CuttlefishConnection | null) => {
    onConnectionChange?.(conn);
  };

  const registerEventHandlers = (handlers: Partial<CuttlefishEventHandlers>) => {
    setEventHandlers((prev) => ({ ...prev, ...handlers }));
  };

  // Update streams when connection changes
  useEffect(() => {
    setStreams(parseStreams(connection));
  }, [connection]);

  // Listen for stream changes to update available streams
  useEffect(() => {
    if (!connection) return;

    const handleStreamChange = () => {
      setStreams(parseStreams(connection));
    };

    connection.onStreamChange(handleStreamChange);

    // Cleanup: remove the stream change callback when unmounting or connection changes
    return () => {
      connection.offStreamChange(handleStreamChange);
    };
  }, [connection]);

  // Set up automatic control message handling (like original implementation)
  useEffect(() => {
    if (!connection) return;

    const handleControlMessage = (message: MessageEvent) => {
      try {
        const messageData: CuttlefishDeviceEvent = JSON.parse(message.data);

        // Call generic control message handler if registered
        eventHandlers.onControlMessage?.(messageData);

        // Handle specific device events
        switch (messageData.event) {
          case 'VIRTUAL_DEVICE_BOOT_STARTED':
            console.info('Device boot started');
            eventHandlers.onDeviceBoot?.();
            break;

          case 'VIRTUAL_DEVICE_SCREEN_CHANGED':
            if (messageData.metadata?.rotation !== undefined) {
              const rotation = Number(messageData.metadata.rotation);
              console.info('Screen rotation changed:', rotation);
              eventHandlers.onScreenChanged?.(rotation);
            }
            break;

          case 'VIRTUAL_DEVICE_CAPTURE_IMAGE':
            console.info('Capture image requested');
            eventHandlers.onCaptureImage?.();
            break;

          case 'VIRTUAL_DEVICE_DISPLAY_POWER_MODE_CHANGED':
            if (messageData.metadata?.display !== undefined && messageData.metadata?.mode !== undefined) {
              const displayId = Number(messageData.metadata.display);
              const mode = String(messageData.metadata.mode);
              console.info(`Display ${displayId} power mode changed to:`, mode);

              // Signal that streams may change
              connection.expectStreamChange();

              eventHandlers.onDisplayPowerModeChanged?.(displayId, mode);
            }
            break;

          default:
            // Unknown events are intentionally ignored.
        }
      } catch (error) {
        console.error('Error parsing control message:', error);
      }
    };

    const handleLightsMessage = (message: MessageEvent) => {
      try {
        const messageData = JSON.parse(message.data);
        eventHandlers.onLightsMessage?.(messageData);
      } catch (error) {
        console.error('Error parsing lights message:', error);
      }
    };

    const handleSensorsMessage = (message: MessageEvent) => {
      try {
        const messageData = JSON.parse(message.data);
        eventHandlers.onSensorsMessage?.(messageData);
      } catch (error) {
        console.error('Error parsing sensors message:', error);
      }
    };

    // Register all message handlers
    connection.onControlMessage(handleControlMessage);
    connection.onLightsMessage(handleLightsMessage);
    connection.onSensorsMessage(handleSensorsMessage);

    console.info('Cuttlefish device event handlers initialized');

    // NOTE: ADB message emitter is initialized in the ADB connection's init() method
    // when getAdb() is first called. This ensures proper initialization order.

    // Cleanup: clear all callbacks when unmounting or connection/handlers change
    return () => {
      // Remove message handlers by setting them to no-op functions
      connection.onControlMessage(() => {});
      connection.onLightsMessage(() => {});
      connection.onSensorsMessage(() => {});

      // Reset ADB emitter (will be recreated on next connection)
      resetAdbMessageEmitter();
    };
  }, [connection, eventHandlers]);

  return (
    <CuttlefishContext.Provider
      value={{
        config,
        connection,
        setConnection,
        streams,
        eventHandlers,
        registerEventHandlers,
      }}
    >
      {children}
    </CuttlefishContext.Provider>
  );
};

export const useCuttlefish = () => {
  const context = useContext(CuttlefishContext);
  if (!context) {
    throw new Error("useCuttlefish must be used within a CuttlefishProvider");
  }
  return context;
};

export const useCuttlefishConnection = () => {
  const { connection } = useCuttlefish();
  // if (!connection) {
  //   throw new Error('No Cuttlefish connection available')
  // }
  return connection;
};
